import {
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBarList,
  messageBarListStylesToast,
  useClassNames,
  useMessageBarList,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  Feature,
  ListFeaturesResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { NameValue } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { NameValueViewer } from '../NameValueViewer/NameValueViewer';
import { IIngestOnlineClassNames, IIngestOnlineStyles, ingestOnlineStylesDefault } from './IngestOnline.styles';

interface IIngestOnlineProps {
  featureSetVersion?: string;
  featureSetId?: string;
  onChange: (jsonString?: string, nameValues?: NameValue[]) => void;
}

export const IngestOnline = ({ featureSetVersion, featureSetId, onChange }: IIngestOnlineProps) => {
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [data, setData] = useState<Feature[]>();
  const { messages, addMessage, removeMessage } = useMessageBarList();
  const refMeta = useRef<{ loaded?: boolean }>({});
  const load = useCallback(async () => {
    setLoadingMessage(`Loading features...`);
    try {
      const { features = [] }: ListFeaturesResponse = await fsApi(CoreService.ListFeatures, {
        featureSetId,
        featureSetVersion,
        pageSize: 1000,
      });
      setData(features);
    } catch (err) {
      const { messageBarType, message } = getErrorMessageBarMessage(
        err,
        `An error has occurred while loading features: `
      );
      addMessage(message as string, undefined, undefined, messageBarType);
    } finally {
      setLoadingMessage('');
    }
  }, []);
  useEffect(() => {
    if (featureSetId && featureSetVersion && !refMeta.current.loaded) {
      load();
      refMeta.current.loaded = true;
    }
  }, [featureSetId, featureSetVersion]);
  const classNames = useClassNames<IIngestOnlineStyles, IIngestOnlineClassNames>(
    'IngestOnline',
    ingestOnlineStylesDefault
  );
  return (
    <div className={classNames.root} data-test="ingestOnline">
      <div className={classNames.messageBar}>
        <MessageBarList
          styles={messageBarListStylesToast}
          messages={messages}
          autoClose
          groupByMessage
          onRemoveMessage={removeMessage}
        />
      </div>
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : (
        <NameValueViewer
          data={data}
          dataTypeField="dataType"
          editable
          onChange={onChange}
          onError={(err, msg) => {
            const { messageBarType, message } = getErrorMessageBarMessage(err, msg);
            addMessage(message as string, undefined, undefined, messageBarType);
          }}
        />
      )}
    </div>
  );
};
